import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { ScienceOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import { useTranslation } from 'react-i18next';
import { getMeasurements } from '../../../../services/measurements';
import { clinicalMeasurementTypes } from '../../../../configs/clinicalMeasurements';
import { patientMeasurementTypes as measurementTypes } from '../../../../configs/measurementTypes';
import {
    clinicalMeasurementsToRows,
    fetchClinicalMeasurements
} from '../../../../services/clinicalMeasurements';
import { useUser } from '../../../../hooks/useUser';
import { agendaTimezone, getAgendaBounds } from '../../Agenda/agendaUtils';

const formatMeasurementValue = (value, unit) => {
    if (value === null || value === undefined || value === '') return '—';
    return `${value} ${unit}`;
};

const columnarDataToRows = (data, type) => {
    const timestamps = Array.isArray(data?.ts) ? data.ts : [];
    const dataKeys = Object.keys(data || {}).filter((key) => key !== 'ts');

    return timestamps
        .map((timestamp, index) => {
            const row = {
                id: `${type}-${timestamp}-${index}`,
                ts: timestamp
            };

            dataKeys.forEach((key) => {
                row[key] = Array.isArray(data[key]) ? data[key][index] : null;
            });

            return row;
        })
        .reverse();
};

const PatientMeasurements = ({ patientUuid }) => {
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const zone = agendaTimezone(user.timezone);
    const [source, setSource] = useState('patient');
    const [selectedType, setSelectedType] = useState(measurementTypes[0].type);
    const [selectedClinicalType, setSelectedClinicalType] = useState(
        clinicalMeasurementTypes[0].type
    );
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [startDate, setStartDate] = useState(dayjs().subtract(1, 'year'));
    const [endDate, setEndDate] = useState(dayjs());
    const availableTypes =
        source === 'patient'
            ? measurementTypes
            : [
                  ...clinicalMeasurementTypes.filter(
                      (item) => item.overviewAsSingleMetric
                  ),
                  ...clinicalMeasurementTypes.filter(
                      (item) => !item.overviewAsSingleMetric
                  )
              ];
    const activeType = source === 'patient' ? selectedType : selectedClinicalType;
    const selectedMeasurement = availableTypes.find(
        (measurement) => measurement.type === activeType
    );
    const validDateRange =
        startDate?.isValid() && endDate?.isValid() && !startDate.isAfter(endDate, 'day');

    dayjs.locale(i18n.language === 'en' ? 'en' : 'pt');

    const patientQuery = useQuery({
        queryKey: [
            'patient-measurements',
            patientUuid,
            selectedType,
            zone,
            startDate?.format('YYYY-MM-DD'),
            endDate?.format('YYYY-MM-DD')
        ],
        queryFn: ({ signal }) =>
            getMeasurements(
                patientUuid,
                selectedType,
                startDate.format('YYYY-MM-DD'),
                endDate.format('YYYY-MM-DD'),
                { timezone: zone, signal }
            ),
        enabled: source === 'patient' && Boolean(patientUuid) && validDateRange,
        staleTime: 60 * 1000
    });
    const bounds = validDateRange
        ? getAgendaBounds(
              {
                  start: startDate.format('YYYY-MM-DD'),
                  end: endDate.format('YYYY-MM-DD')
              },
              zone
          )
        : {};
    const clinicalQuery = useQuery({
        queryKey: [
            'patient-measurements-v2',
            patientUuid,
            'DOCTOR',
            selectedMeasurement.apiType,
            bounds.timestamp,
            bounds.endTimestamp
        ],
        queryFn: ({ signal }) =>
            fetchClinicalMeasurements({
                patient: patientUuid,
                type: selectedMeasurement.apiType,
                timestamp: bounds.timestamp,
                endTimestamp: bounds.endTimestamp,
                signal
            }),
        enabled: source === 'clinician' && Boolean(patientUuid) && validDateRange,
        staleTime: 60 * 1000
    });
    const { data, isPending, isError, refetch } =
        source === 'patient' ? patientQuery : clinicalQuery;

    const rows = useMemo(
        () =>
            source === 'patient'
                ? columnarDataToRows(data, selectedType)
                : clinicalMeasurementsToRows(data || [], selectedMeasurement),
        [data, selectedType, source, selectedMeasurement]
    );

    const columns = useMemo(
        () => [
            {
                field: 'ts',
                headerName: t('userPage.measurements.columns.date'),
                minWidth: 210,
                flex: 1.3,
                valueFormatter: (value) =>
                    value && dayjs(value).isValid()
                        ? dayjs(value).format('DD/MM/YYYY, HH:mm:ss')
                        : '—'
            },
            ...selectedMeasurement.fields.map((item) => ({
                field: item.field,
                headerName: item.label || t(item.labelKey),
                minWidth: 160,
                flex: 1,
                valueFormatter: (value) => formatMeasurementValue(value, item.unit)
            })),
            ...[
                {
                    field: 'notes',
                    headerName: t('userPage.measurements.columns.notes'),
                    minWidth: 240,
                    flex: 1.5,
                    valueFormatter: (value) =>
                        value === null || value === undefined || value === ''
                            ? '—'
                            : value
                }
            ]
        ],
        [selectedMeasurement, t]
    );

    return (
        <Card
            variant="outlined"
            sx={{
                borderColor: '#E0E5E9',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'stretch', md: 'center' },
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        gap: 3,
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            flex: 1
                        }}
                    >
                        <Typography fontWeight={700}>
                            {t('userPage.measurements.addedBy')}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={source}
                            aria-label={t('userPage.measurements.sourceSelector')}
                            onChange={(_, value) => {
                                if (!value) return;
                                setSource(value);
                                setPaginationModel((current) => ({
                                    ...current,
                                    page: 0
                                }));
                            }}
                            sx={{
                                gap: 1,
                                '& .MuiToggleButton-root': {
                                    height: 40,
                                    px: 2.5,
                                    border: 0,
                                    borderRadius: '5px !important',
                                    bgcolor: '#F4F6F8',
                                    color: '#1976D2',
                                    fontWeight: 700,
                                    textTransform: 'none'
                                },
                                '& .MuiToggleButton-root.Mui-selected': {
                                    bgcolor: '#1976D2',
                                    color: '#FFFFFF',
                                    '&:hover': { bgcolor: '#1565C0' }
                                }
                            }}
                        >
                            <ToggleButton value="patient">
                                {t('userPage.measurements.patientSource')}
                            </ToggleButton>
                            <ToggleButton value="clinician">
                                {t('userPage.measurements.clinicianSource')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={i18n.language === 'en' ? 'en' : 'pt'}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                flexWrap: 'wrap',
                                gap: 1.5
                            }}
                        >
                            <DatePicker
                                label={t('userPage.measurements.startDate')}
                                value={startDate}
                                onChange={(value) => {
                                    setStartDate(value);
                                    setPaginationModel((current) => ({
                                        ...current,
                                        page: 0
                                    }));
                                }}
                                maxDate={endDate || dayjs()}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: { size: 'small', sx: { width: 185 } }
                                }}
                            />
                            <DatePicker
                                label={t('userPage.measurements.endDate')}
                                value={endDate}
                                onChange={(value) => {
                                    setEndDate(value);
                                    setPaginationModel((current) => ({
                                        ...current,
                                        page: 0
                                    }));
                                }}
                                minDate={startDate || undefined}
                                maxDate={dayjs()}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: { size: 'small', sx: { width: 185 } }
                                }}
                            />
                        </Box>
                    </LocalizationProvider>
                </Box>

                <Tabs
                    value={activeType}
                    onChange={(_, value) => {
                        if (source === 'patient') setSelectedType(value);
                        else setSelectedClinicalType(value);
                        setPaginationModel((current) => ({ ...current, page: 0 }));
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label={t('userPage.measurements.metricSelector')}
                    sx={{
                        minWidth: 0,
                        minHeight: 42,
                        mb: 2,
                        '& .MuiTabs-indicator': { height: 3 },
                        '& .MuiTab-root': {
                            minHeight: 42,
                            minWidth: 0,
                            px: 1.75,
                            fontWeight: 600,
                            textTransform: 'none'
                        }
                    }}
                >
                    {availableTypes.map((measurement) => {
                        const Icon = measurement.icon || ScienceOutlined;
                        return (
                            <Tab
                                key={measurement.type}
                                value={measurement.type}
                                icon={<Icon fontSize="small" />}
                                iconPosition="start"
                                label={t(measurement.labelKey)}
                            />
                        );
                    })}
                </Tabs>

                {!validDateRange ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('userPage.measurements.invalidDateRange')}
                    </Alert>
                ) : isError ? (
                    <Alert
                        severity="error"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => refetch()}
                            >
                                {t('userPage.overview.measurements.retry')}
                            </Button>
                        }
                    >
                        {t('userPage.measurements.loadError')}
                    </Alert>
                ) : (
                    <Box sx={{ height: 510, width: '100%' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={isPending}
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            localeText={{
                                noRowsLabel: t('userPage.measurements.noRecords')
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-columnHeaders': {
                                    borderRadius: 1
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#FFFFFF'
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: '#FFFFFF',
                                    fontWeight: 700
                                },
                                '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton':
                                    {
                                        color: '#FFFFFF'
                                    },
                                '& .MuiDataGrid-row:nth-of-type(even)': {
                                    bgcolor: '#FAFBFC'
                                },
                                '& .MuiDataGrid-cell': { borderColor: '#EEF1F3' }
                            }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

PatientMeasurements.propTypes = {
    patientUuid: PropTypes.string.isRequired
};

export default PatientMeasurements;
